import React, { FC } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { capitalizeFirstLetter, colors, IAiChart, ITwoLevelPieChartConfig } from "./chartConfig";
import { useStyles } from "./AiGraphs.styles";

const AiTwoLevelPieChart: FC<IAiChart> = ({ config, chartData }) => {
  const styles = useStyles();

  const { data } = config as ITwoLevelPieChartConfig;

  const level1Key = data.level1.field;
  const level2Key = data.level2.field;
  const valueKey = data.values.field;

  const sectorData = chartData.reduce((acc, curr) => {
    const existing = acc.find((s: any) => s[level1Key] === curr[level1Key]);
    if (existing) {
      existing[valueKey] += curr[valueKey];
    } else {
      acc.push({ [level1Key]: curr[level1Key], [valueKey]: curr[valueKey] });
    }
    return acc;
  }, []);

  const sectorColors: Record<string, string> = {};
  sectorData.forEach((s: any, idx: any) => {
    sectorColors[s[level1Key]] = colors[idx % colors.length];
  });

  const subsectorData = chartData.map((d) => ({
    ...d,
    color: sectorColors[d[level1Key]],
  }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        <Legend
          wrapperStyle={{ paddingTop: 30 }}
          content={({ payload }) => (
            <div className={styles.legendContainer}>
              {payload?.map((entry, index) => (
                <div key={`item-${index}`} className={styles.legendWrapper}>
                  <span
                    className={styles.legendIcon}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>{capitalizeFirstLetter(entry.value ?? "")}</span>
                </div>
              ))}
            </div>
          )}
        />

        <Tooltip
          formatter={(val, name, props) => [
            val,
            props.payload[level2Key] || props.payload[level1Key],
          ]}
        />

        <Pie
          data={sectorData}
          dataKey={valueKey}
          nameKey={level1Key}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          label
        >
          {sectorData.map((entry: any, idx: any) => (
            <Cell key={idx} fill={sectorColors[entry[level1Key]]} />
          ))}
        </Pie>

        <Pie
          data={subsectorData}
          dataKey={valueKey}
          nameKey={level2Key}
          cx="50%"
          cy="50%"
          innerRadius={105}
          outerRadius={150}
          label
        >
          {subsectorData.map((entry, idx) => (
            <Cell key={idx} fill={entry.color} opacity={0.7} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

export default AiTwoLevelPieChart;
